// Excel Files Store - Gestión de archivos Excel/CSV globales del workflow
import { create } from 'zustand';
import { withToast, ToastMessages } from '../lib/toastHelper';
import type { ExcelFile } from '../types/excel';
import { findMatchingAgentFile } from '../lib/excelFileSync';
// [H-FE009] Import estático — no hay dependencia circular con agentClient
import { agentClient } from '../lib/agentClient';

export interface ExcelFileSyncFailure {
  fileId: string;
  fileName: string;
  reason: string;
}

export interface ExcelFileSyncSummary {
  success: boolean;
  syncedCount: number;
  reusedCount: number;
  failedFiles: ExcelFileSyncFailure[];
}

interface ExcelFilesStore {
  files: ExcelFile[];
  addFile: (file: ExcelFile) => void;
  setFiles: (files: ExcelFile[]) => void;
  removeFile: (id: string) => Promise<void>;
  updateFile: (id: string, updates: Partial<ExcelFile>) => void;
  getFile: (id: string) => ExcelFile | undefined;
  getFileByName: (name: string) => ExcelFile | undefined;
  clearFiles: () => void;
  syncFileWithAgent: (id: string) => Promise<boolean>; // Sincronizar archivo con agente
  syncAllFilesWithAgent: () => Promise<ExcelFileSyncSummary>; // Sincronizar todos los archivos pendientes
}

export const useExcelFilesStore = create<ExcelFilesStore>((set, get) => ({
  files: [],

  addFile: (file: ExcelFile) => {
    set((state) => ({
      files: [...state.files, file],
    }));
  },

  setFiles: (files: ExcelFile[]) => {
    set({ files });
  },

  removeFile: async (id: string) => {
    const file = get().files.find((f) => f.id === id);
    
    await withToast(
      async () => {
        // Si el archivo está sincronizado con el agente, intentar eliminarlo también
        if (file?.syncedWithAgent && file.filePath) {
          try {
            await agentClient.deleteFile(file.filePath);
          } catch (error) {
            console.warn('Error eliminando archivo del agente:', error);
            // Continuar con la eliminación local aunque falle en el agente
          }
        }
        
        set((state) => ({
          files: state.files.filter((f) => f.id !== id),
        }));
      },
      {
        success: ToastMessages.excel.delete.success,
        error: ToastMessages.excel.delete.error,
      }
    );
  },

  updateFile: (id: string, updates: Partial<ExcelFile>) => {
    set((state) => ({
      files: state.files.map((f) =>
        f.id === id ? { ...f, ...updates } : f
      ),
    }));
  },

  getFile: (id: string) => {
    return get().files.find((f) => f.id === id);
  },

  getFileByName: (name: string) => {
    return get().files.find((f) => f.name === name);
  },

  clearFiles: () => {
    set({ files: [] });
  },

  syncFileWithAgent: async (id: string) => {
    const file = get().files.find((f) => f.id === id);
    if (!file || file.syncedWithAgent) {
      return true; // Ya está sincronizado
    }

    // withToast maneja el toast de éxito/error internamente.
    // Si el callback lanza, withToast muestra el toast de error y re-lanza.
    try {
      await withToast(
        async () => {
          const fileExtension = file.name.match(/\.(csv|xlsx|xls)$/i)?.[1]?.toLowerCase() || 'csv';
          const fileType = fileExtension === 'csv' ? 'csv' : (fileExtension === 'xlsx' ? 'xlsx' : 'xls');

          const saveResult = await agentClient.saveFile(file.name, file.fileContent, fileType);

          if (saveResult.success && saveResult.filePath) {
            const updates: Partial<ExcelFile> = {
              filePath: saveResult.filePath,
              relativePath: saveResult.relativePath,
              localPath: file.localPath || saveResult.filePath, // Auto-guardar ruta para no recargar
              syncedWithAgent: true,
            };

            // Si el agente retorno headers y el frontend no los pudo parsear
            // (archivos XLSX no se parsean localmente), usar los del agente
            if (saveResult.headers?.length && !file.headers.length) {
              updates.headers = saveResult.headers;
            }
            if (saveResult.totalRows !== undefined && file.totalRows === 0) {
              updates.totalRows = saveResult.totalRows;
            }

            get().updateFile(id, updates);
            return;
          }
          throw new Error('Error al guardar archivo en el agente');
        },
        {
          success: `${ToastMessages.excel.sync.success}: ${file.name}`,
          error: `${ToastMessages.excel.sync.error}: ${file.name}`,
        }
      );
      return true;
    } catch {
      // withToast ya mostró el toast de error; solo retornamos false
      return false;
    }
  },

  syncAllFilesWithAgent: async () => {
    const pendingFiles = get().files.filter((f) => !f.syncedWithAgent);
    if (pendingFiles.length === 0) {
      return {
        success: true,
        syncedCount: 0,
        reusedCount: 0,
        failedFiles: [],
      };
    }

    // Manejar error de listFiles como no-fatal (404/501 = "no hay archivos previos")
    let agentFiles: Awaited<ReturnType<typeof agentClient.listFiles>>['files'] = [];
    let agentListAvailable = false;
    try {
      const listResult = await agentClient.listFiles();
      if (!listResult.success) {
        console.warn('No se pudo verificar archivos existentes en el agente');
      } else {
        agentListAvailable = true;
      }
      agentFiles = listResult.success ? listResult.files : [];
    } catch (listError) {
      console.warn('Error al consultar archivos del agente, se continuará sin lista previa:', listError);
    }

    let syncedCount = 0;
    let reusedCount = 0;
    const failedFiles: ExcelFileSyncFailure[] = [];

    for (const file of pendingFiles) {
      const existingAgentFile = findMatchingAgentFile(file, agentFiles);
      if (existingAgentFile) {
        get().updateFile(file.id, {
          filePath: existingAgentFile.path,
          relativePath: existingAgentFile.relativePath,
          localPath: file.localPath || existingAgentFile.path, // Auto-guardar ruta
          syncedWithAgent: true,
        });
        reusedCount += 1;
        continue;
      }

      // Si tiene localPath, no necesita upload — el agente lee directo del disco
      if (file.localPath) {
        get().updateFile(file.id, { syncedWithAgent: true });
        reusedCount += 1;
        continue;
      }

      if (!file.fileContent) {
        failedFiles.push({
          fileId: file.id,
          fileName: file.name,
          reason: agentListAvailable
            ? 'El archivo ya no está en memoria y no existe en el agente. Vuelve a cargarlo.'
            : 'No se pudo verificar el agente y el archivo ya no está en memoria. Vuelve a cargarlo.',
        });
        continue;
      }

      const synced = await get().syncFileWithAgent(file.id);
      if (synced) {
        syncedCount += 1;
      } else {
        failedFiles.push({
          fileId: file.id,
          fileName: file.name,
          reason: 'No se pudo sincronizar el archivo con el agente.',
        });
      }
    }

    return {
      success: failedFiles.length === 0,
      syncedCount,
      reusedCount,
      failedFiles,
    };
  },
}));
